import React, { useState, useRef, useEffect } from 'react'
import { useParams } from 'react-router'
import { useSelector } from 'react-redux'
import flowRight from 'lodash/flowRight'
import get from 'lodash/get'

import { Text } from '@fielded/shared-ui'
import { toast } from '@fielded/shared-ui/src/components/Page'

import withConfig from '../../../../../van-shared/hoc/withConfig'
import { withApi } from '../../../../../common/ApiProvider'
import { withUser } from '../../../../../common/AuthenticationProvider'
import { selectUserCountry } from '../../../../../root/reducers/common/location'
import { arrayBufferFileReader } from '@fielded/shared-ui/src/components/ImportButton'
import PSMInboundShipmentsOverview from './PSMInboundShipmentsOverview'
import { SHIPMENT_STATUS } from '@fielded/fs-api/lib/shipment/constants'
import { getUppercaseTenant } from '../../../../../van-shared/utils/utils'

export const VIEW_TYPES = {
  DOWNLOAD: 'download',
  UPLOAD: 'upload'
}

const getSubNavigationItems = (view) => {
  return [
    {
      title: 'Download template',
      path: `/shipments/list/overview/${VIEW_TYPES.DOWNLOAD}`,
      active: !view || view === VIEW_TYPES.DOWNLOAD
    },
    {
      title: 'Upload shipment',
      path: `/shipments/list/overview/${VIEW_TYPES.UPLOAD}`,
      active: view === VIEW_TYPES.UPLOAD
    }
  ]
}

const PSMInboundShipmentsOverviewContainer = ({
  api,
  config,
  user,
  history
}) => {
  const country = useSelector(selectUserCountry) || get(user, 'location.country')

  const [isDownloading, setIsDownloading] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [warehouseCode, setWarehouseCode] = useState()
  const [facilities, setFacilities] = useState([])
  const [products, setProducts] = useState([])

  const documentFormRef = useRef()
  const { view } = useParams()
  const warehouseTenant = getUppercaseTenant(config)

  const initialize = async () => {
    try {
      const locationId = get(user, 'location.id')
      const userPrograms = get(user, 'programs', [])

      const [locationObject, relatedFacilities, fetchedProducts] = await Promise.all([
        api.location.get(locationId),
        api.location.listRelated(locationId, { includeSelf: false }),
        userPrograms.length ? api.product.listForPrograms(userPrograms) : Promise.resolve([])
      ])

      const currentWarehouseCode = get(locationObject, 'additionalData.warehouseCode')

      const filteredFacilities = (relatedFacilities || []).filter((place) => {
        const isPSMWarehouse = get(place, 'additionalData.isPSMWarehouse')
        const hasWarehouseCode = get(place, 'additionalData.warehouseCode')
        return !isPSMWarehouse && hasWarehouseCode
      })

      setWarehouseCode(currentWarehouseCode)
      setFacilities(filteredFacilities)
      setProducts(fetchedProducts)
    } catch (error) {
      console.error('Error initializing inbound shipment data:', error)
    }
  }

  useEffect(() => {
    initialize()
  }, [])

  const onDownloadTemplate = async () => {
    setIsDownloading(true)

    try {
      const data = await api.shipment.getImportTemplate()
      const blob = new global.Blob([
        new global.Uint8Array(data)
      ], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      })

      setIsDownloading(false)
      toast({
        title: 'Template downloaded',
        type: 'success',
        children: <Text>Open the file in Excel, update the required details, and upload it when ready.</Text>
      })
      return global.URL.createObjectURL(blob)
    } catch (error) {
      console.error(error)
    }
  }

  const onHandleUpload = async (importData) => {
    try {
      setIsUploading(true)
      const response = await api.shipment.importShipmentsFromSheet(importData, {
        useFunderSuffixForWarehouse: false,
        validateManufacturer: false,
        newStatus: SHIPMENT_STATUS.RECEIVED,
        doCheckSent: false,
        warehouseTenant
      })

      if (response) {
        toast({
          title: 'Upload Successful',
          type: 'success',
          children: <Text>Your shipment data is being processed.</Text>
        })
        documentFormRef.current.reset()
      }
    } catch (error) {
      const defaultErrorTitle = 'Upload Failed'
      const defaultErrorMessage = 'There was an issue with your upload. Check the file format and required fields, then try again.'
      const errorMessage = error.invalidRows ? error.invalidRows[0].message : defaultErrorMessage
      toast({
        title: error.message || defaultErrorTitle,
        type: 'warning',
        children: <Text>{errorMessage}</Text>,
        autoDismiss: 10000
      })
      console.error(error)
    } finally {
      setIsUploading(false)
    }
  }

  const onUploadTemplate = async () => {
    const documentValidationErrors = await documentFormRef.current.validate()
    if (documentValidationErrors) {
      return
    }

    const files = documentFormRef.current.getFiles()
    const file = files.file[0]
    arrayBufferFileReader(file, { onImported: onHandleUpload })
  }

  const onCancel = () => {
    history.goBack()
  }

  return (
    <PSMInboundShipmentsOverview
      view={view}
      config={config}
      history={history}
      country={country}
      subNavigationItems={getSubNavigationItems(view)}
      documentFormRef={documentFormRef}
      isDownloading={isDownloading}
      isUploading={isUploading}
      onDownloadTemplate={onDownloadTemplate}
      onUploadTemplate={onUploadTemplate}
      onCancel={onCancel}
      warehouseCode={warehouseCode}
      facilities={facilities}
      products={products}
      user={user}
    />
  )
}

const withHOCs = flowRight(
  withApi,
  withConfig,
  withUser
)

export default withHOCs(PSMInboundShipmentsOverviewContainer)
